import { Box, Grid, Typography } from '@mui/material';
import { SchemaField } from '@okletsplay/core';
import { DynamicSchemaField } from 'shared/components/Form/DynamicSchemaField';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Info } from 'shared/components/Info';
import { useStyles } from './DynamicSchemaBlock.styles';

interface ComponentProps {
  properties: Record<string, SchemaField>;
  rootPropertyName: string;
  variant?: 'create' | 'edit';
  isEditAllowed?: boolean;
}

type SchemaFieldMap = Record<number, [string, SchemaField][]>;

export const DynamicSchemaBlock: FC<ComponentProps> = ({
  properties,
  rootPropertyName,
  variant,
  isEditAllowed = true,
  children,
}) => {
  const { classes } = useStyles();
  const { control, setValue } = useFormContext();

  const fieldsMap = useMemo((): SchemaFieldMap => {
    const result: SchemaFieldMap = {};
    const orderedProperties = Object.entries(properties).sort(([, field1], [, field2]) => {
      return (
        (field1.visibilityConfig?.group || 0) - (field2.visibilityConfig?.group || 0) ||
        (field1.visibilityConfig?.order || 0) - (field2.visibilityConfig?.order || 0)
      );
    });

    orderedProperties.forEach(([fieldName, field]) => {
      const visibilityConfig = field.visibilityConfig;
      if (!visibilityConfig?.onCreateVisible) return;

      const group = visibilityConfig.group !== undefined ? visibilityConfig.group : -1;

      if (result[group]) {
        result[group].push([fieldName, field]);
      } else {
        result[group] = [[fieldName, field]];
      }
    });

    return result;
  }, [properties]);

  const lastGroup = useMemo(() => {
    return Math.max(...Object.keys(fieldsMap).map((key) => Number(key)));
  }, [fieldsMap]);

  const getFieldControlName = useCallback(
    (fieldName: string) => `${rootPropertyName}.${fieldName}`,
    [rootPropertyName]
  );

  useEffect(() => {
    Object.entries(properties).forEach(([fieldName, field]) => {
      if (field.defaultValue) {
        setValue(getFieldControlName(fieldName), field.defaultValue);
      }
    });
  }, [getFieldControlName, properties, setValue]);

  const renderField = (fieldName: string, config: SchemaField) => {
    return (
      <Grid item flex={1} xs={12} sm={6} md={4} key={fieldName}>
        <Controller
          control={control}
          name={getFieldControlName(fieldName)}
          render={({ field: { onChange, value } }) => (
            <>
              <Box className={classes.fieldHeader}>
                <Typography>{config.label}</Typography>
                {config?.visibilityConfig?.tooltip && (
                  <Info>{config?.visibilityConfig?.tooltip}</Info>
                )}
              </Box>
              <DynamicSchemaField
                field={config}
                onChange={onChange}
                value={value}
                isEditable={
                  isEditAllowed &&
                  (variant === 'create' || Boolean(config.visibilityConfig.isEditable))
                }
              />
            </>
          )}
        />
      </Grid>
    );
  };

  return (
    <Grid container className={classes.grid} spacing={2}>
      {Object.entries(fieldsMap).map(([group, fields]) => {
        const groupFields = fields.map((field) => renderField(...field));

        const isLastGroup = Number(group) === lastGroup;

        if (isLastGroup) {
          groupFields.unshift(children as JSX.Element);
        }

        return (
          <Grid
            className={classes.groupContainer}
            container
            spacing={2}
            key={group}
            mb={isLastGroup ? 0 : 2}
          >
            {groupFields}
          </Grid>
        );
      })}
    </Grid>
  );
};
